import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CreditCard, Receipt, Download, AlertTriangle, XCircle, Crown, Apple, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Product ID to tier mapping for deriving plan name from subscription
const PRODUCT_TO_TIER: Record<string, string> = {
  // Dodo GBP
  'pdt_0NVuY6d81OSmgrAbHUmha': 'pro',
  'pdt_0NVuXsHu0EihXz2nBGTqc': 'business',
  // Dodo EUR
  'pdt_0NVuXft4491tMkfTFvEMb': 'pro',
  'pdt_0NVuVg0VeBqhQBMwwdoRP': 'business',
  // Dodo USD
  'pdt_0NVuFhglNQQMIweQdYbyN': 'pro',
  'pdt_0NVuFXdFvQJZyLgUqEoi5': 'business',
  // Dodo INR
  'pdt_0NVqYqNZwJ9uheowQCVnO': 'pro',
  'pdt_0NVqinNpdZ0yTYrNHwKGk': 'business',
  // Apple IAP
  'DClite': 'pro',
  'DCBuisness': 'business',
  // Dodo Pro One-Time
  'pdt_0NYGFf7egQ8khfnyFUPIJ': 'pro',  // GBP
  'pdt_0NYGFmsqESS4TLb717hJe': 'pro',  // EUR
  'pdt_0NYJVzT84VJPBKKoyMK4w': 'pro',  // USD
  'pdt_0NYGFUTmAsz4aYqMxDQNj': 'pro',  // INR
};

interface Payment {
  payment_id?: string;
  transaction_id?: string;
  total_amount?: number;
  currency?: string;
  status?: string;
  created_at?: string;
  purchase_date?: string;
  product_cart?: Array<{ name: string }>;
  product_id?: string;
  payment_method?: string;
  subscription_id?: string;
}

interface Subscription {
  subscription_id?: string;
  original_transaction_id?: string;
  status: string;
  next_billing_date?: string;
  expires_date?: string;
  previous_billing_date?: string;
  recurring_pre_tax_amount?: number;
  currency?: string;
  subscription_period_interval?: string;
  payment_frequency_interval?: string;
  product_id?: string;
  cancel_at_next_billing_date?: boolean;
  cancelled_at?: string | null;
  will_auto_renew?: boolean;
}

interface BillingData {
  payment_provider?: 'apple' | 'dodo';
  manage_url?: string;
  customer: { customer_id: string; email: string } | null;
  active_subscription: Subscription | null;
  recent_payments: Payment[];
  all_subscriptions: Subscription[];
  has_payment_history: boolean;
}

interface PackPurchase {
  id: string;
  pack: string | null;
  product_id: string | null;
  credits_awarded: number;
  credits_remaining: number;
  status: string;
  created_at: string;
  provider: string;
}

interface BillingDetailsProps {
  userTier: string;
  refreshKey?: number;
}

export const BillingDetails = ({ userTier, refreshKey = 0 }: BillingDetailsProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [allPayments, setAllPayments] = useState<Payment[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [packPurchases, setPackPurchases] = useState<PackPurchase[]>([]);

  useEffect(() => {
    fetchBillingData();
    fetchCreditsAndPacks();
  }, [refreshKey]);

  const fetchCreditsAndPacks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const [{ data: profile }, { data: purchases }] = await Promise.all([
        supabase
          .from('profiles')
          .select('bonus_generations, plan_name')
          .eq('id', session.user.id)
          .maybeSingle(),
        supabase
          .from('processed_payments')
          .select('id, pack, product_id, credits_awarded, credits_remaining, status, created_at, provider')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false }),
      ]);

      if (profile) {
        setCredits(profile.bonus_generations ?? 0);
        setPlanName(profile.plan_name ?? null);
      }
      setPackPurchases((purchases as PackPurchase[]) ?? []);
    } catch (err) {
      console.error('Failed to load credits/purchases:', err);
    }
  };

  const fetchBillingData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError("Please log in to view billing details");
        return;
      }

      // Fetch overview data
      const { data: overviewData, error: overviewError } = await supabase.functions.invoke(
        "customer-billing",
        { body: { action: "get-overview" } }
      );

      if (overviewError) throw overviewError;
      setBillingData(overviewData);

      // Fetch all payments for the full history
      const { data: paymentsData, error: paymentsError } = await supabase.functions.invoke(
        "customer-billing",
        { body: { action: "get-payments" } }
      );

      if (paymentsError) throw paymentsError;
      
      // Filter to only show successful payments
      const successfulPayments = (paymentsData.payments || []).filter(
        (p: Payment) => p.status === 'succeeded' || p.status === 'completed'
      );
      setAllPayments(successfulPayments);
    } catch (err: any) {
      console.error("Error fetching billing data:", err);
      setError(err.message || "Failed to load billing details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!billingData?.active_subscription) return;
    
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-billing", {
        body: {
          action: "cancel-subscription",
          subscriptionId: billingData.active_subscription.subscription_id,
        },
      });

      if (error) throw error;

      toast.success(data.message || "Subscription cancelled successfully");
      fetchBillingData(); // Refresh data
    } catch (err: any) {
      console.error("Error cancelling subscription:", err);
      toast.error(err.message || "Failed to cancel subscription");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDownloadInvoice = async (paymentId: string) => {
    setIsDownloading(paymentId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please log in to download invoices");
      }

      // Fetch the PDF directly using fetch to get binary data
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-billing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: "get-invoice", paymentId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }

      // Get the PDF as a blob
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded");
    } catch (err: any) {
      console.error("Error downloading invoice:", err);
      toast.error(err.message || "Failed to download invoice");
    } finally {
      setIsDownloading(null);
    }
  };

  const formatCurrency = (amount: number | undefined, currency: string | undefined) => {
    if (amount === undefined || currency === undefined) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Assuming amount is in cents
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Paid</Badge>;
      case 'active':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Cancelled</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get plan name from subscription's product_id, falling back to userTier prop
  const getPlanNameFromSubscription = (sub: Subscription | null, fallbackTier: string): string => {
    if (sub?.product_id) {
      const tier = PRODUCT_TO_TIER[sub.product_id];
      if (tier) {
        return `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`;
      }
    }
    // Fallback to userTier prop if no product mapping found
    if (fallbackTier && fallbackTier !== 'free') {
      return `${fallbackTier.charAt(0).toUpperCase() + fallbackTier.slice(1)} Plan`;
    }
    return 'Subscription';
  };

  if (isLoading) {
    return (
      <Card className="p-6 shadow-card mb-6">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-6 h-6 text-primary" />
          <h3 className="text-xl font-bold">Billing & Payments</h3>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 shadow-card mb-6">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-6 h-6 text-primary" />
          <h3 className="text-xl font-bold">Billing & Payments</h3>
        </div>
        <div className="text-center py-8">
          <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={fetchBillingData} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  const subscription = billingData?.active_subscription;
  const lastPack = packPurchases.find((p) => p.status === 'active') ?? packPurchases[0] ?? null;
  const isPaidEntitled = userTier !== 'free' && (credits ?? 0) > 0;
  const hasPayments = allPayments.length > 0;
  const isAppleSubscription = billingData?.payment_provider === 'apple';
  const manageUrl = billingData?.manage_url;

  return (
    <Card className="p-6 shadow-card mb-6">
      <div className="flex items-center gap-3 mb-6">
        {isAppleSubscription ? (
          <Apple className="w-6 h-6 text-primary" />
        ) : (
          <CreditCard className="w-6 h-6 text-primary" />
        )}
        <h3 className="text-xl font-bold">Billing & Payments</h3>
        {isAppleSubscription && (
          <Badge variant="secondary" className="ml-auto">
            <Apple className="w-3 h-3 mr-1" />
            App Store
          </Badge>
        )}
      </div>

      {/* Credits & Entitlements */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Your Credits
        </h4>
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-lg">
                {credits === null ? '—' : `${credits} credits available`}
              </p>
              <p className="text-sm text-muted-foreground">
                {lastPack
                  ? `Last purchase: ${lastPack.pack === 'boost' ? 'Boost' : 'Refill'} • ${lastPack.credits_awarded} credits (one-time purchase)`
                  : 'Trial credits — buy a credit pack any time'}
              </p>
            </div>
            {planName && <Badge variant="secondary">{planName}</Badge>}
          </div>
          <div className="grid sm:grid-cols-3 gap-2 text-sm">
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-muted-foreground text-xs">Auto-save retention</p>
              <p className="font-medium">{isPaidEntitled ? '60 days' : '7 days'}</p>
            </div>
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-muted-foreground text-xs">Design styles</p>
              <p className="font-medium">{isPaidEntitled ? '90+ in 14 categories' : 'Limited'}</p>
            </div>
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-muted-foreground text-xs">Downloads</p>
              <p className="font-medium">{isPaidEntitled ? 'Watermark-free' : 'Watermarked'}</p>
            </div>
          </div>
          <Button
            onClick={() => window.location.href = '/pricing'}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <Crown className="w-4 h-4 mr-2" />
            Buy credits
          </Button>
        </div>
      </div>

      {/* Legacy subscription — only shown for existing subscribers */}
      {subscription && (
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Your Subscription
        </h4>
        
        {subscription ? (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-lg">
                  {getPlanNameFromSubscription(subscription, userTier)}
                </p>
                {isAppleSubscription ? (
                  <p className="text-sm text-muted-foreground">
                    Managed by App Store
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(subscription.recurring_pre_tax_amount, subscription.currency)} / {subscription.payment_frequency_interval?.toLowerCase() || 'month'}
                  </p>
                )}
              </div>
              {getStatusBadge(subscription.cancel_at_next_billing_date ? 'cancelled' : subscription.status)}
            </div>

            {/* Expiry/renewal date */}
            {isAppleSubscription && subscription.expires_date && (
              <p className="text-sm text-muted-foreground">
                {subscription.will_auto_renew ? 'Renews' : 'Expires'}: {formatDate(subscription.expires_date)}
              </p>
            )}
            {!isAppleSubscription && subscription.next_billing_date && !subscription.cancel_at_next_billing_date && (
              <p className="text-sm text-muted-foreground">
                Next billing: {formatDate(subscription.next_billing_date)}
              </p>
            )}

            {subscription.cancel_at_next_billing_date && subscription.next_billing_date && (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <p className="text-sm text-yellow-600">
                  Subscription ends on {formatDate(subscription.next_billing_date)}
                </p>
              </div>
            )}

            {/* Auto-renew disabled warning for Apple */}
            {isAppleSubscription && subscription.will_auto_renew === false && (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <p className="text-sm text-yellow-600">
                  Auto-renewal is off. Subscription will expire on {subscription.expires_date ? formatDate(subscription.expires_date) : 'the end of your billing period'}.
                </p>
              </div>
            )}

            {/* Apple: Manage in App Store button */}
            {isAppleSubscription && manageUrl && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => window.open(manageUrl, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Manage in App Store
              </Button>
            )}

            {/* Dodo: Cancel subscription button */}
            {!isAppleSubscription && subscription.status === 'active' && !subscription.cancel_at_next_billing_date && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-2">
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your subscription will remain active until the end of your current billing period
                      {subscription.next_billing_date && (
                        <> ({formatDate(subscription.next_billing_date)})</>
                      )}. After that, you'll be downgraded to the free tier.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelSubscription}
                      disabled={isCancelling}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      {isCancelling ? "Cancelling..." : "Cancel Subscription"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ) : null}
      </div>
      )}

      <Separator className="my-6" />

      {/* Payment History Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Payment History
          </h4>
        </div>

        {hasPayments ? (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  {!isAppleSubscription && <TableHead>Amount</TableHead>}
                  <TableHead>Status</TableHead>
                  {!isAppleSubscription && <TableHead className="text-right">Invoice</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allPayments.map((payment, index) => {
                  const paymentId = payment.payment_id || payment.transaction_id || `tx-${index}`;
                  const paymentDate = payment.created_at || payment.purchase_date || '';
                  const productName = payment.product_cart?.[0]?.name || 
                    (payment.product_id ? PRODUCT_TO_TIER[payment.product_id] : null) ||
                    "Subscription Payment";
                  const displayName = typeof productName === 'string' && productName !== 'pro' && productName !== 'business' 
                    ? productName 
                    : `${productName.charAt(0).toUpperCase() + productName.slice(1)} Plan`;
                    
                  return (
                    <TableRow key={paymentId}>
                      <TableCell className="font-medium">
                        {paymentDate ? formatDate(paymentDate) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {displayName}
                      </TableCell>
                      {!isAppleSubscription && (
                        <TableCell>
                          {formatCurrency(payment.total_amount, payment.currency)}
                        </TableCell>
                      )}
                      <TableCell>
                        {getStatusBadge(payment.status || 'completed')}
                      </TableCell>
                      {!isAppleSubscription && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoice(paymentId)}
                            disabled={isDownloading === paymentId}
                          >
                            <Download className="w-4 h-4" />
                            <span className="sr-only">Download Invoice</span>
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="border rounded-lg p-8 text-center">
            <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No payment history yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isAppleSubscription 
                ? "Your App Store transaction history will appear here"
                : "Your successful payments will appear here"
              }
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};
